import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Tour IDs - unique identifiers for each tour
 */
export const TOUR_IDS = {
  ADD_CLIENT_MODAL: 'add-client-modal',
  CONFIGURE_CLIENT: 'configure-client',
  COVERAGES: 'coverages',
  CERTIFICATES: 'certificates',
  ADD_SUBCONTRACTOR: 'add-subcontractor',
  SUBCONTRACTOR_DETAILS: 'subcontractor-details',
  REQUEST_DOCUMENTS: 'request-documents',
  DOCHUB: 'dochub',
  DASHBOARD: 'dashboard',
};

/**
 * Tour display titles
 */
export const TOUR_TITLES = {
  [TOUR_IDS.ADD_CLIENT_MODAL]: 'New Client - Tour Progress',
  [TOUR_IDS.CONFIGURE_CLIENT]: 'Configure Client - Tour Progress',
  [TOUR_IDS.COVERAGES]: 'Coverages - Tour Progress',
  [TOUR_IDS.CERTIFICATES]: 'Certificates - Tour Progress',
  [TOUR_IDS.ADD_SUBCONTRACTOR]: 'Add Subcontractor - Tour Progress',
  [TOUR_IDS.SUBCONTRACTOR_DETAILS]: 'Subcontractor Details - Tour Progress',
  [TOUR_IDS.REQUEST_DOCUMENTS]: 'Request Documents - Tour Progress',
  [TOUR_IDS.DOCHUB]: 'DocHub - Tour Progress',
  [TOUR_IDS.DASHBOARD]: 'Dashboard - Tour Progress',
};

/**
 * Default styles for Joyride tours
 */
export const DEFAULT_TOUR_STYLES = {
  options: {
    arrowColor: '#fff',
    backgroundColor: '#fff',
    beaconSize: 36,
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    primaryColor: '#1976d2',
    spotlightShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
    textColor: '#333',
    width: 380,
    zIndex: 10000,
  },
  tooltipContainer: {
    textAlign: 'left',
  },
  tooltipTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
  },
  tooltipContent: {
    fontSize: 14,
    lineHeight: 1.5,
  },
  buttonNext: {
    backgroundColor: '#1976d2',
    fontSize: 14,
    padding: '8px 16px',
  },
  buttonBack: {
    color: '#1976d2',
    fontSize: 14,
  },
  buttonSkip: {
    color: '#666',
    fontSize: 13,
  },
};

/**
 * Custom Joyride Tooltip Component with MANACERT logo next to close button
 */
export const CustomTooltip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
  size,
}) => (
  <Box
    {...tooltipProps}
    sx={{
      backgroundColor: '#fff',
      borderRadius: 2,
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      maxWidth: 380,
      minWidth: 290,
      padding: 0,
    }}
  >
    {/* Header with title, logo, and close button */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        pb: 1,
        borderBottom: step.title ? '1px solid #eee' : 'none',
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          fontSize: 16,
          color: '#333',
          flex: 1,
        }}
      >
        {step.title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <img
          src="/app-logo-h110.png"
          alt="MANACERT"
          style={{
            height: 24,
            opacity: 0.85,
          }}
        />
        <IconButton
          {...closeProps}
          size="small"
          sx={{
            color: '#666',
            '&:hover': { color: '#333', backgroundColor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>

    {/* Content */}
    <Box sx={{ px: 2, py: 1.5 }}>
      {step.content}
    </Box>

    {/* Footer with navigation buttons */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        pt: 1,
        borderTop: '1px solid #eee',
      }}
    >
      <Box>
        {index > 0 && (
          <Typography
            component="button"
            {...skipProps}
            sx={{
              background: 'none',
              border: 'none',
              color: '#666',
              fontSize: 13,
              cursor: 'pointer',
              '&:hover': { color: '#333' },
            }}
          >
            Skip Tour
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {index > 0 && (
          <Typography
            component="button"
            {...backProps}
            sx={{
              background: 'none',
              border: 'none',
              color: '#1976d2',
              fontSize: 14,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Back
          </Typography>
        )}
        <Typography
          component="button"
          {...primaryProps}
          sx={{
            backgroundColor: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: 1,
            fontSize: 14,
            padding: '8px 16px',
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#1565c0' },
          }}
        >
          {isLastStep ? 'Finish' : `Next (Step ${index + 1} of ${size})`}
        </Typography>
      </Box>
    </Box>
  </Box>
);

/**
 * Tour content wrapper component for consistent styling
 */
const TourContent = ({ children }) => (
  <Box sx={{ py: 1 }}>
    {children}
  </Box>
);


/**
 * Add Client Modal Tour Configuration
 * Guides users through required vs optional fields when adding a new client
 */
export const ADD_CLIENT_MODAL_TOUR = {
  id: TOUR_IDS.ADD_CLIENT_MODAL,
  steps: [
    {
      target: '.add-client-name-field',
      title: 'Required: Client Name',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Client Name</strong> is the only required field to create a new client.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter a clear, identifiable name for the organization.
          </Typography>
        </TourContent>
      ),
      disableBeacon: true,
      placement: 'bottom',
      spotlightPadding: 8,
    },
    {
      target: '.add-client-optional-fields',
      title: 'Optional Information',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            All remaining fields are <strong>optional</strong> and can help you maintain comprehensive records:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Website URL</strong> - Company website</Typography></li>
            <li><Typography variant="body2"><strong>Administrator</strong> - Primary contact name &amp; email</Typography></li>
            <li><Typography variant="body2"><strong>Business Phone</strong> - Contact number</Typography></li>
            <li><Typography variant="body2"><strong>Address</strong> - Business location details</Typography></li>
            <li><Typography variant="body2"><strong>Logo</strong> - Company branding</Typography></li>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            You can always add or update this information later.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 12,
    },
  ],
};

/**
 * Configure Client Tour Configuration
 * Guides users through essential client settings after creating a new client
 */
export const CONFIGURE_CLIENT_TOUR = {
  id: TOUR_IDS.CONFIGURE_CLIENT,
  steps: [
    {
      target: '.settings-page-container',
      title: 'Welcome to Client Settings',
      content: (
        <TourContent>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <img
              src="/app-logo-h110.png"
              alt="MANACERT Logo"
              style={{ height: 75, marginBottom: 8 }}
            />
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Let's configure your new client. We'll walk through the <strong>essential settings</strong> to get you started.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This tour will guide you through the key configuration steps.
          </Typography>
        </TourContent>
      ),
      disableBeacon: true,
      placement: 'center',
      spotlightPadding: 0,
      data: { tab: 'details' },
    },
    {
      target: '.settings-menu-details',
      title: 'Organization Details',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Details</strong> tab contains organization information and certificate holder details.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The address information here is used to verify against the <strong>Certificate Holder</strong> field within uploaded COIs.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Note: The Certificate Holder name may differ from the Organization Name.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'details' },
    },
    {
      target: '.details-certificate-holder',
      title: 'Certificate Holder Information',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Enter your <strong>Certificate Holder</strong> information exactly as it should appear on COI documents.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This is the address the system will <strong>compare against</strong> when validating uploaded certificates.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ensure accuracy to enable proper certificate verification.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 12,
      data: { tab: 'details' },
    },
    {
      target: '.settings-menu-users',
      title: 'Add Team Members',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Users</strong> tab lets you add team members who will manage this client.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            When you add a user, they receive an <strong>email invitation</strong> with a temporary password to register their account.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            They will set their permanent password upon first login.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'users' },
    },
    {
      target: '.users-add-button',
      title: 'Add New User',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Click the <strong>+ icon</strong> to open the Add User dialog.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Let's walk through the required and optional fields.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'users', openAddUserModal: true },
    },
    {
      target: '.add-user-email-field',
      title: 'Required: Email Address',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Email Address</strong> is the only required field to invite a user.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This email becomes their username for logging into the system.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { tab: 'users', keepModalOpen: true },
    },
    {
      target: '.add-user-optional-fields',
      title: 'Optional: Name Fields',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>First Name</strong> and <strong>Last Name</strong> are optional but recommended.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            These help identify users in the system and can be added later.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { tab: 'users', keepModalOpen: true },
    },
    {
      target: '.add-user-role-field',
      title: 'User Role Selection',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Assign a <strong>role</strong> to control the user's access level:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Admin</strong> - Full management access</Typography></li>
            <li><Typography variant="body2"><strong>Viewer</strong> - Read-only access</Typography></li>
            <li><Typography variant="body2"><strong>PM Role</strong> - Project manager permissions</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { tab: 'users', keepModalOpen: true, closeModalAfter: true },
    },
    {
      target: '.settings-menu-templates',
      title: 'Notification Email Templates',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Customize <strong>email templates</strong> for automated notifications sent to vendors.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Let's walk through each notification type.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'templates' },
    },
    {
      target: '.email-tab-reminder-1',
      title: '1 Day Reminder',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>1 Day Reminder</strong> is sent when a certificate is expiring tomorrow.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This is the final urgent reminder before expiration.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'templates' },
    },
    {
      target: '.email-tab-reminder-7',
      title: '7 Day Reminder',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>7 Day Reminder</strong> is sent one week before certificate expiration.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gives vendors time to obtain and upload renewed certificates.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'templates' },
    },
    {
      target: '.email-tab-reminder-14',
      title: '14 Day Reminder',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>14 Day Reminder</strong> is sent two weeks before certificate expiration.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The earliest reminder, allowing ample time for certificate renewal.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'templates' },
    },
    {
      target: '.email-tab-request',
      title: 'Certificate Upload Request',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Certificate Upload Request</strong> is sent when you request a new certificate from a vendor.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Used for initial certificate requests or when requesting updated documentation.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'templates' },
    },
    {
      target: '.email-tab-expiration',
      title: 'Certificate Expiration',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Certificate Expiration</strong> is sent when a certificate has officially expired.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Notifies vendors that their certificate is no longer valid and requires immediate action.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'templates' },
    },
    {
      target: '.email-template-editor',
      title: 'Template Editor',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Each template has a <strong>subject line</strong> and <strong>email body</strong> you can customize.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use variables like [Vendor Name] and [Project Name] to personalize messages automatically.
          </Typography>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 12,
      data: { tab: 'templates' },
    },
    {
      target: '.settings-menu-config',
      title: 'Configuration Settings',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Configuration</strong> tab contains important customization options.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click here to customize labels and notification settings.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'configuration' },
    },
    {
      target: '.config-default-project',
      title: 'Enforce Coverage Requirements',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            When <strong>enabled</strong>, this setting ensures that all vendors will always use the <strong>Client Default</strong> coverage requirements.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This guarantees consistent coverage standards across all vendors, regardless of project-specific settings.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 12,
      data: { tab: 'configuration' },
    },
    {
      target: '.config-labels-section',
      title: 'Customize Labels',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Customize how <strong>Vendors</strong> and <strong>Coverages</strong> are labeled throughout the app.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2">Vendor Label (e.g., "Subcontractor", "Contractor")</Typography></li>
            <li><Typography variant="body2">Coverage Label (e.g., "Project", "Job", "Site")</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 12,
      data: { tab: 'configuration' },
    },
    {
      target: '.config-notification-email',
      title: 'Notification Email',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Set up a <strong>primary email address</strong> for system notifications.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Receive alerts about certificate expirations and compliance updates.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 12,
      data: { tab: 'configuration' },
    },
    {
      target: '.settings-menu-activity',
      title: 'Activity History',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Activity History</strong> tab provides a complete audit trail of all changes made to this client.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track user invitations, configuration updates, and other important events.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'activity_history' },
    },
    {
      target: '.settings-page-container',
      title: 'Setup Complete!',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Your client is now configured and ready to use.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can always return to Settings to make changes or explore additional options.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
      data: { tab: 'details' },
    },
  ],
};

/**
 * Coverages Tour Configuration
 * Guides users through the Client Default coverage entry and explains coverage types
 */
export const COVERAGES_TOUR = {
  id: TOUR_IDS.COVERAGES,
  steps: [
    {
      target: '.project-details-screen',
      title: 'Welcome to Coverage Requirements',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This is the <strong>Client Default</strong> coverage entry - your starting point for defining insurance requirements.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This default coverage cannot be deleted but can be customized to match your organization's needs.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
      data: { tab: 'coverages' },
    },
    {
      target: '.coverage-project-header',
      title: 'Client Default Coverage',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Client Default Project</strong> serves as your baseline coverage template.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All new vendors will inherit these coverage requirements unless assigned to a specific project with different requirements.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 12,
      data: { tab: 'coverages' },
    },
    {
      target: '.coverage-menu-coverages',
      title: 'Required Coverages Tab',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Required Coverages</strong> tab is where you define the insurance requirements for this coverage.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set minimum limits, required coverage types, and validation rules here.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'coverages' },
    },
    {
      target: '.coverage-description-section',
      title: 'Description of Operation Validation',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Use this field to specify required text that must appear in the <strong>Description of Operations</strong> section of uploaded certificates.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The system will perform a verbatim check to ensure compliance.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'coverages' },
    },
    {
      target: '.coverage-type-tabs',
      title: 'Insurance Type Tabs',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Coverage requirements are organized by <strong>insurance type</strong>. Each tab represents a different type of coverage.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Let's walk through each standard insurance type.
          </Typography>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 8,
      data: { tab: 'coverages' },
    },
    {
      target: '.coverage-tab-gl',
      title: 'GL - General Liability',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Commercial General Liability (GL)</strong> covers third-party bodily injury, property damage, and personal injury claims.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This is typically the most essential coverage requirement for vendors.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'coverages', coverageTab: 0 },
    },
    {
      target: '.coverage-tab-au',
      title: 'AU - Automobile Liability',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Automobile Liability (AU)</strong> covers vehicles used in business operations.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Required when vendors operate vehicles on your behalf or at your locations.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'coverages', coverageTab: 1 },
    },
    {
      target: '.coverage-tab-umb',
      title: 'UMB - Umbrella Liability',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Umbrella Liability (UMB)</strong> provides additional coverage above the limits of underlying policies.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Offers extra protection for high-risk situations or large claims.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'coverages', coverageTab: 2 },
    },
    {
      target: '.coverage-tab-wc',
      title: 'WC - Workers Compensation',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Workers Compensation (WC)</strong> covers employee injuries and illnesses that occur on the job.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Required in most states when vendors have employees.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'coverages', coverageTab: 3 },
    },
    {
      target: '.coverage-update-button',
      title: 'Adding Custom Coverage Types',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Need additional coverage types? Click <strong>Update</strong> to add custom insurance requirements beyond the standard GL, AU, UMB, and WC.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Custom types can include Professional Liability, Cyber Insurance, or industry-specific coverages.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'coverages' },
    },
    {
      target: '.coverage-menu-subcontractors',
      title: 'Subcontractors Tab',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Subcontractors</strong> tab shows all vendors assigned to this coverage.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add or remove vendors and track their compliance status from here.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'subcontractors' },
    },
    {
      target: '.coverage-menu-certificates',
      title: 'Certificates Tab',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Certificates</strong> tab displays all certificates associated with this coverage.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review certificate details, expiration dates, and compliance status.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'certificates' },
    },
    {
      target: '.project-details-screen',
      title: 'Tour Complete!',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You now understand how to configure coverage requirements for your organization.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Remember: The Client Default coverage serves as your baseline - customize it to match your organization's insurance requirements.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
      data: { tab: 'coverages' },
    },
  ],
};

/**
 * Certificates Tour Configuration
 * Guides users through the certificates table, columns, and actions
 */
export const CERTIFICATES_TOUR = {
  id: TOUR_IDS.CERTIFICATES,
  steps: [
    {
      target: '.certificates-container',
      title: 'Welcome to Certificates',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Certificates</strong> page displays all insurance certificates uploaded by your vendors.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor compliance, review documents, and manage certificate statuses from this central location.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
    },
    {
      target: '.status-filter',
      title: 'Status Filter Tabs',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Filter certificates by their <strong>compliance status</strong>:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>All</strong> - View all certificates</Typography></li>
            <li><Typography variant="body2"><strong>Approved</strong> - Meets requirements</Typography></li>
            <li><Typography variant="body2"><strong>Review</strong> - Needs attention</Typography></li>
            <li><Typography variant="body2"><strong>Expired</strong> - Past expiration date</Typography></li>
            <li><Typography variant="body2"><strong>Requested</strong> - Awaiting upload</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
    },
    {
      target: '.cert-column-id',
      title: 'Certificate ID',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            A unique <strong>identifier</strong> for each certificate.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click on a row to view full certificate details.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
    },
    {
      target: '.cert-column-type',
      title: 'Certificate Type',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Indicates the <strong>type</strong> of certificate:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Standard</strong> - Single certificate from one insurer</Typography></li>
            <li><Typography variant="body2"><strong>Multi-Agent</strong> - Combined coverage from multiple insurers</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
    },
    {
      target: '.cert-column-status',
      title: 'COI Status',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The current <strong>compliance status</strong> of the certificate:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Review</strong> - Needs manual review</Typography></li>
            <li><Typography variant="body2"><strong>Approved</strong> - Meets all requirements</Typography></li>
            <li><Typography variant="body2"><strong>Not Approved</strong> - Does not meet requirements</Typography></li>
            <li><Typography variant="body2"><strong>Expired</strong> - Coverage has lapsed</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
    },
    {
      target: '.cert-column-vendor',
      title: 'Subcontractor',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>vendor or subcontractor</strong> who provided this certificate.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Links to the vendor's profile for more details.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
    },
    {
      target: '.cert-column-subcontractor-status',
      title: 'Subcontractor Status',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>overall compliance status</strong> of the subcontractor:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Compliant</strong> - All coverage requirements are met</Typography></li>
            <li><Typography variant="body2"><strong>Not Compliant</strong> - Missing or insufficient coverage</Typography></li>
            <li><Typography variant="body2"><strong>Pending</strong> - Awaiting certificate submission</Typography></li>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This status is calculated based on the subcontractor's active certificates and your coverage requirements.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
    },
    {
      target: '.cert-column-producer',
      title: 'Producer',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>insurance producer or agency</strong> that issued the certificate.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Useful for verifying the source of coverage.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
    },
    {
      target: '.cert-column-dates',
      title: 'Important Dates',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Key dates for tracking certificate lifecycle:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Certificate Date</strong> - When issued</Typography></li>
            <li><Typography variant="body2"><strong>Last Modified</strong> - Last update</Typography></li>
            <li><Typography variant="body2"><strong>Policy EXP</strong> - Expiration date</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
    },
    {
      target: '.cert-column-scan',
      title: 'Scan Status',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Shows the <strong>automated scan result</strong> from our OCR system.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The system extracts coverage details and validates them against your requirements.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
    },
    {
      target: '.cert-action-flag',
      title: 'Flag Certificate',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Click the <strong>flag icon</strong> to mark a certificate for follow-up.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Flagged certificates appear in your flagged items for easy tracking.
          </Typography>
        </TourContent>
      ),
      placement: 'left',
      spotlightPadding: 8,
    },
    {
      target: '.cert-action-share',
      title: 'Share Certificate',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Click the <strong>share icon</strong> to send the certificate via email.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Useful for requesting updates or sharing with stakeholders.
          </Typography>
        </TourContent>
      ),
      placement: 'left',
      spotlightPadding: 8,
    },
    {
      target: '.cert-action-expand',
      title: 'Supplemental Documents',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Click the <strong>expand button</strong> to view supplemental documents.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The badge shows the number of attached documents. Expand to view, download, or review endorsements and additional coverage files.
          </Typography>
        </TourContent>
      ),
      placement: 'left',
      spotlightPadding: 8,
    },
    {
      target: '.certificates-container',
      title: 'Tour Complete!',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You now know how to navigate and manage certificates effectively.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click any row to view full certificate details, or use the action buttons to flag, share, or view documents.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
    },
  ],
};

/**
 * Add Subcontractor Tour Configuration
 * Guides users through the Add Subcontractor wizard fields
 */
export const ADD_SUBCONTRACTOR_TOUR = {
  id: TOUR_IDS.ADD_SUBCONTRACTOR,
  steps: [
    // Step 1: Subcontractor Information
    {
      target: '.add-vendor-wizard-content',
      title: 'Add Subcontractor Wizard',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Welcome to the <strong>Add Subcontractor</strong> wizard!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This wizard guides you through adding a new subcontractor in 3 steps. Required fields are marked with an asterisk (*).
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
      disableOverlay: true,
      data: { wizardStep: 0 },
    },
    {
      target: '.add-vendor-stepper',
      title: 'Wizard Steps',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The wizard has <strong>3 steps</strong>:
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Subcontractor Information</strong> - Required contact details</Typography></li>
            <li><Typography variant="body2"><strong>Insurance Agent</strong> - Optional agent info</Typography></li>
            <li><Typography variant="body2"><strong>Request Certificates</strong> - Optional email request</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 0 },
    },
    {
      target: '.vendor-field-name',
      title: 'Subcontractor Name (Required)',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Enter the <strong>company or business name</strong> of the subcontractor.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This name will appear on certificates and in your vendor list.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 0 },
    },
    {
      target: '.vendor-field-entity-name',
      title: 'Use Entity Name (Optional)',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Check this if the <strong>legal entity name</strong> differs from the business name.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            For example, if "ABC Plumbing" operates under "ABC Plumbing Services LLC".
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 0 },
    },
    {
      target: '.vendor-field-email',
      title: 'Email Address (Required)',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>primary email</strong> for certificate requests and notifications.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This is where certificate request emails will be sent.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 0 },
    },
    {
      target: '.vendor-field-phone',
      title: 'Phone Number (Optional)',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The subcontractor's <strong>contact phone number</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Useful for follow-ups on certificate requests.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 0 },
    },
    {
      target: '.vendor-field-additional-emails',
      title: 'Additional Emails (Optional)',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Add <strong>additional email addresses</strong> to receive certificate requests.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Type an email and press Enter to add. Multiple emails can be added.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 0 },
    },
    {
      target: '.vendor-field-coverages',
      title: 'Associate with Coverages (Required)',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Select which <strong>coverage requirements</strong> apply to this subcontractor.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use "Select All" to quickly associate with all available coverages. The subcontractor's certificates will be validated against these requirements.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 0 },
    },
    {
      target: '.vendor-field-address',
      title: 'Address Fields (Optional)',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Enter the subcontractor's <strong>business address</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Address information helps with certificate holder verification and record keeping.
          </Typography>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 0 },
    },
    {
      target: '.vendor-field-logo',
      title: 'Subcontractor Logo (Optional)',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Upload the subcontractor's <strong>company logo</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Supports PNG, JPG, and JPEG formats. The logo appears in the vendor profile.
          </Typography>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 0 },
    },
    // Step 2: Insurance Agent Information
    {
      target: '.insurance-agent-form',
      title: 'Step 2: Insurance Agent Information',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This step is <strong>optional</strong>. Add the subcontractor's insurance agent details here.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If provided, the insurance agent can receive certificate request emails directly.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 1 },
    },
    {
      target: '.agent-field-name',
      title: 'Agent Name',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The insurance agent's <strong>name or agency name</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you provide a name, you must also provide an email (and vice versa).
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 1 },
    },
    {
      target: '.agent-field-contact',
      title: 'Agent Contact',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The agent's <strong>email and phone</strong> for certificate communications.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Email is required if you provided an agent name.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 1 },
    },
    {
      target: '.agent-field-address',
      title: 'Agent Address',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The insurance agent's <strong>business address</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All address fields are optional.
          </Typography>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 1 },
    },
    // Step 3: Request Certificates
    {
      target: '.request-certificates-form',
      title: 'Step 3: Request Certificates',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This step is <strong>optional</strong>. Automatically send a certificate request email when the subcontractor is created.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Skip this step if you want to request certificates later.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 2 },
    },
    {
      target: '.request-project-select',
      title: 'Select Coverage/Project',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Choose which <strong>coverage requirements</strong> to include in the certificate request.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The selected project's coverage requirements will be shown to the subcontractor.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 2 },
    },
    {
      target: '.request-recipients',
      title: 'Email Recipients',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Select who should receive the <strong>certificate request email</strong>:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Subcontractor</strong> - Primary contact email</Typography></li>
            <li><Typography variant="body2"><strong>Insurance Agent</strong> - If agent info was provided</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 2 },
    },
    {
      target: '.add-vendor-navigation',
      title: 'Complete the Wizard',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Click <strong>Finish</strong> to create the subcontractor.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you selected recipients in Step 3, certificate request emails will be sent automatically.
          </Typography>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 8,
      disableOverlay: true,
      data: { wizardStep: 2 },
    },
  ],
};

/**
 * Subcontractor Details Tour Configuration
 * Guides users through the subcontractor details page, vertical tabs, and information panels
 */
export const SUBCONTRACTOR_DETAILS_TOUR = {
  id: TOUR_IDS.SUBCONTRACTOR_DETAILS,
  steps: [
    // Welcome
    {
      target: '.vendor-details-container',
      title: 'Subcontractor Details',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Welcome to the <strong>Subcontractor Details</strong> page!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage all information about this subcontractor, including certificates, documents, and activity history.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
    },
    // Status indicator
    {
      target: '.vendor-status-indicator',
      title: 'Compliance Status',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>compliance status</strong> shows whether this subcontractor meets your insurance requirements.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Compliant</strong> - All requirements met</Typography></li>
            <li><Typography variant="body2"><strong>Not Compliant</strong> - Missing or insufficient coverage</Typography></li>
            <li><Typography variant="body2"><strong>Archived</strong> - No longer active</Typography></li>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click to change the status.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
    },
    // Request Documents button
    {
      target: '.vendor-request-documents-btn',
      title: 'Request Documents',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Click to <strong>request documents</strong> from this subcontractor.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can request a Certificate of Insurance (COI) or other documents. An email will be sent with an upload link.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
    },
    // Details tab
    {
      target: '.vendor-menu-details',
      title: 'Details Tab',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Details</strong> tab shows the subcontractor's activity dashboard and contact information.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This is the default view when you open a subcontractor.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'details' },
    },
    // Activity Dashboard
    {
      target: '.vendor-activity-dashboard',
      title: 'Activity Dashboard',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Activity Dashboard</strong> shows a visual timeline of this subcontractor's activity.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Requests</strong> - Document requests sent</Typography></li>
            <li><Typography variant="body2"><strong>Reminders</strong> - Follow-up reminders</Typography></li>
            <li><Typography variant="body2"><strong>Certificate Uploads</strong> - COIs submitted</Typography></li>
            <li><Typography variant="body2"><strong>Other Documents</strong> - Additional files</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 'details' },
    },
    // Subcontractor Information panel
    {
      target: '.vendor-info-panel',
      title: 'Subcontractor Information',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This panel displays the subcontractor's <strong>contact and address information</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the Edit button to update details or Delete to remove this subcontractor.
          </Typography>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 8,
      data: { tab: 'details' },
    },
    // Certificates tab
    {
      target: '.vendor-menu-certificates',
      title: 'Certificates Tab',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Certificates</strong> tab lists all insurance certificates for this subcontractor.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View certificate details, check compliance status, and add new certificates.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'certificates' },
    },
    // DocHub Sub tab
    {
      target: '.vendor-menu-dochub',
      title: 'DocHub Sub Tab',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>DocHub Sub</strong> tab shows all supplemental documents uploaded by this subcontractor.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This includes endorsements, policy documents, and other supporting files.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'dochub' },
    },
    // Coverages/Requirements tab
    {
      target: '.vendor-menu-requirements',
      title: 'Coverages Tab',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Coverages</strong> tab shows which coverage requirements are assigned to this subcontractor.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can also manage notification settings here.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'requirements' },
    },
    // Activities tab
    {
      target: '.vendor-menu-activities',
      title: 'Activities Tab',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Activities</strong> tab shows a detailed log of all actions taken on this subcontractor.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track status changes, email notifications, certificate updates, and more.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 'activities' },
    },
    // Tour complete
    {
      target: '.vendor-details-container',
      title: 'Tour Complete!',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You now know how to navigate and manage subcontractor details.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the vertical tabs to switch between sections, and the Request Documents button to request new certificates.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
      data: { tab: 'details' },
    },
  ],
};

/**
 * Tour: Request Documents workflow
 * Triggered from RequestTypeModal - explains both Certificate and Other Documents flows
 */
export const REQUEST_DOCUMENTS_TOUR = {
  id: TOUR_IDS.REQUEST_DOCUMENTS,
  steps: [
    // Step 1: Welcome
    {
      target: '.request-type-modal',
      title: 'Request Documents',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Use this dialog to request documents from your subcontractor.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can request insurance certificates (ACORD forms) or other types of documents like policies, contracts, and licenses.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 20,
      disableOverlay: true,
      data: { modal: 'request-type' },
    },
    // Step 2: Certificate option
    {
      target: '.request-type-certificate',
      title: 'Certificate Request',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Certificate</strong> - Request insurance certificates (ACORD forms).
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use this option when you need proof of insurance coverage. The subcontractor will receive an email with a secure link to upload their ACORD 25 form or similar certificate.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 10,
      disableOverlay: true,
      data: { modal: 'request-type' },
    },
    // Step 3: Other Documents option
    {
      target: '.request-type-other',
      title: 'Other Documents Request',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Other Documents</strong> - Request policies, contracts, licenses, and more.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use this option when you need supporting documents beyond standard insurance certificates. This includes full policies, endorsements, contracts, leases, and driver's licenses.
          </Typography>
        </TourContent>
      ),
      placement: 'left',
      spotlightPadding: 10,
      disableOverlay: true,
      data: { modal: 'request-type' },
    },
    // Step 4: Switch to Other Documents modal - Step 1
    {
      target: '.document-request-modal',
      title: 'Other Documents Workflow',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            When requesting other documents, you'll follow a guided wizard process.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The wizard helps you specify exactly what documents you need and provides clear instructions to the subcontractor.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 20,
      disableOverlay: true,
      data: { modal: 'other-documents', wizardStep: 0 },
    },
    // Step 5: Stepper
    {
      target: '.document-request-stepper',
      title: 'Progress Stepper',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The stepper shows your progress through the request workflow.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Steps may vary based on which document types you select.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 10,
      disableOverlay: true,
      data: { modal: 'other-documents', wizardStep: 0 },
    },
    // Step 6: Document type selection
    {
      target: '.document-type-selection',
      title: 'Select Document Types',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Choose the types of documents you need from the subcontractor.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Available types include:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
            <Typography component="li" variant="body2" color="text.secondary">Policy - Insurance policies</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Endorsement - Policy amendments</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Contract - Service agreements</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Lease - Lease agreements</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Driver's License - For operators</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Other - Any other documents</Typography>
          </Box>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 10,
      disableOverlay: true,
      data: { modal: 'other-documents', wizardStep: 0 },
    },
    // Step 7: Add Details (wizardStep 1 - since CONTRACT doesn't need certificate selection)
    {
      target: '.document-request-content',
      title: 'Provide Details',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Add specific instructions for each document type you selected.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Be clear and specific about what you need. These instructions will be included in the email sent to the subcontractor.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            Note: For Policy, Endorsement, or Other document types, an additional step allows you to associate existing certificates.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 10,
      disableOverlay: true,
      data: { modal: 'other-documents', wizardStep: 1 },
    },
    // Step 8: Review & Send (wizardStep 2)
    {
      target: '.document-request-content',
      title: 'Review & Send',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Review your request before sending.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You'll see a summary of:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
            <Typography component="li" variant="body2" color="text.secondary">Vendor information</Typography>
            <Typography component="li" variant="body2" color="text.secondary">All requested document types</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Your instructions for each type</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Associated certificates (if any)</Typography>
          </Box>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 10,
      disableOverlay: true,
      data: { modal: 'other-documents', wizardStep: 2 },
    },
    // Step 9: Navigation buttons
    {
      target: '.document-request-actions',
      title: 'Navigation',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Use the navigation buttons to move through the wizard.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click <strong>Send Request</strong> when you're ready. The subcontractor will receive an email with a secure link to upload documents (expires in 7 days).
          </Typography>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 10,
      disableOverlay: true,
      data: { modal: 'other-documents', wizardStep: 2 },
    },
    // Step 10: Tour Complete
    {
      target: '.request-type-modal',
      title: 'Tour Complete!',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You now understand the Request Documents workflow.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use <strong>Certificate</strong> for insurance certificates, or <strong>Other Documents</strong> for policies, contracts, and other supporting documents.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 20,
      disableOverlay: true,
      data: { modal: 'request-type' },
    },
  ],
};

/**
 * Tour: DocHub - Document management hub
 * Guides users through List view and File Cabinet features
 */
export const DOCHUB_TOUR = {
  id: TOUR_IDS.DOCHUB,
  steps: [
    // Step 1: Welcome
    {
      target: '.dochub-container',
      title: 'Welcome to DocHub',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>DocHub</strong> is your central document management hub.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View, organize, and manage all certificates and documents for your subcontractors in one place.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
      data: { tab: 0 },
    },
    // Step 2: Tab Navigation
    {
      target: '.dochub-tabs',
      title: 'View Options',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Switch between two views:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>List</strong> - Table view of all documents with sorting and filtering</Typography></li>
            <li><Typography variant="body2"><strong>File Cabinet</strong> - Organize documents into folders</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 0 },
    },
    // Step 3: Filters Button
    {
      target: '.dochub-filters-btn',
      title: 'Filter Documents',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Click <strong>Filters</strong> to narrow down your document list.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Filter by date range, status, document type, subcontractor, and expiration date.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 0 },
    },
    // Step 4: Search Box
    {
      target: '.dochub-search',
      title: 'Search Documents',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Quick search</strong> across all documents.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Search by document name, subcontractor name, or document ID.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 0 },
    },
    // Step 5: Export CSV
    {
      target: '.dochub-export-btn',
      title: 'Export Documents',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Export to CSV</strong> for reporting and analysis.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Downloads the current view (with applied filters) as a spreadsheet.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 0 },
    },
    // Step 6: Column Selector
    {
      target: '.dochub-column-selector',
      title: 'Customize Columns',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Choose which columns to display in the table.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Show or hide columns like Category, Folder Path, and more to focus on what matters most.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 0 },
    },
    // Step 7: Document Table
    {
      target: '.dochub-table',
      title: 'Document List',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>document table</strong> shows all your certificates and documents.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2">Click column headers to sort</Typography></li>
            <li><Typography variant="body2">Drag column edges to resize</Typography></li>
            <li><Typography variant="body2">Click document name to view details</Typography></li>
            <li><Typography variant="body2">Expired dates shown in red</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 8,
      data: { tab: 0 },
    },
    // Step 8: Document Actions
    {
      target: '.dochub-actions-column',
      title: 'Document Actions',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Each document has quick actions:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Flag</strong> - Mark important documents</Typography></li>
            <li><Typography variant="body2"><strong>Menu</strong> - View, download, share, add to folder, or delete</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'left',
      spotlightPadding: 8,
      data: { tab: 0 },
    },
    // Step 9: File Cabinet Tab
    {
      target: '.dochub-file-cabinet-tab',
      title: 'File Cabinet',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Switch to <strong>File Cabinet</strong> to organize documents into folders.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create custom folder structures, drag and drop documents, and keep everything organized.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 0 },
    },
    // Step 10: File Cabinet Overview (after switching tabs)
    {
      target: '.file-cabinet-container',
      title: 'File Cabinet View',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>File Cabinet</strong> provides folder-based organization.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create folders, subfolders, and organize documents by project, vendor, year, or any structure you prefer.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
      data: { tab: 1 },
    },
    // Step 11: Create Folder
    {
      target: '.file-cabinet-create-folder',
      title: 'Create Folders',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Click <strong>Create Folder</strong> to add a new folder.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Folders can be nested to create a hierarchy. Right-click folders for more options.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { tab: 1 },
    },
    // Step 12: Folder Tree
    {
      target: '.file-cabinet-folder-tree',
      title: 'Folder Navigation',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>folder tree</strong> shows your folder structure.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2">Click to expand/collapse folders</Typography></li>
            <li><Typography variant="body2">Drag documents from the list to add to folders</Typography></li>
            <li><Typography variant="body2">Right-click for folder actions (rename, delete, etc.)</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { tab: 1 },
    },
    // Step 13: Folder Contents
    {
      target: '.file-cabinet-contents',
      title: 'Folder Contents',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            View the contents of the selected folder here.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click documents to view, or use the action menu to download, share, or remove from folder.
          </Typography>
        </TourContent>
      ),
      placement: 'left',
      spotlightPadding: 8,
      data: { tab: 1 },
    },
    // Step 14: Tour Complete
    {
      target: '.dochub-container',
      title: 'Tour Complete!',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You now know how to use <strong>DocHub</strong>!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the List view for quick access and filtering. Use File Cabinet to keep documents organized in folders.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
      data: { tab: 0 },
    },
  ],
};

/**
 * Tour: Dashboard - Overview of all dashboard features
 * Guides users through Activity, Compliance, Expiring COIs, and Email Activity
 */
export const DASHBOARD_TOUR = {
  id: TOUR_IDS.DASHBOARD,
  steps: [
    // Step 1: Welcome
    {
      target: '.dashboard-container',
      title: 'Welcome to Dashboard',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The <strong>Dashboard</strong> is your central hub for monitoring activity and compliance.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Access key metrics, track certificates, and stay on top of expiring COIs.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
      data: { verticalTab: 'my-activity' },
    },
    // Step 2: Vertical Navigation
    {
      target: '.dashboard-side-menu',
      title: 'Dashboard Navigation',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Navigate between different dashboard views:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Activity</strong> - Your work items and recent actions</Typography></li>
            <li><Typography variant="body2"><strong>Compliance</strong> - Vendor and certificate compliance charts</Typography></li>
            <li><Typography variant="body2"><strong>Expiring COIs</strong> - Certificates expiring soon</Typography></li>
            <li><Typography variant="body2"><strong>Email Activity</strong> - Email tracking and performance</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { verticalTab: 'my-activity' },
    },
    // Step 3: Activity Dashboard Stats
    {
      target: '.activity-dashboard-stats',
      title: 'Activity Summary',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Quick overview of your client's activity:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Requests</strong> - Certificate requests sent</Typography></li>
            <li><Typography variant="body2"><strong>Reminders</strong> - Follow-up emails sent</Typography></li>
            <li><Typography variant="body2"><strong>Certificate Uploads</strong> - New certificates received</Typography></li>
            <li><Typography variant="body2"><strong>Other Documents</strong> - Additional documents uploaded</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { verticalTab: 'my-activity' },
    },
    // Step 4: Activity Chart
    {
      target: '.activity-dashboard-chart',
      title: 'Activity Timeline',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Visual timeline of activity over time.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track trends in requests, reminders, uploads, and documents. Click the legend to show/hide specific metrics.
          </Typography>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 8,
      data: { verticalTab: 'my-activity' },
    },
    // Step 5: Activity Sub-tabs
    {
      target: '.activity-tabs',
      title: 'Activity Tabs',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Switch between different activity views:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Items in Review</strong> - Certificates awaiting review</Typography></li>
            <li><Typography variant="body2"><strong>Flagged Items</strong> - Items you've flagged for attention</Typography></li>
            <li><Typography variant="body2"><strong>Recent Activity</strong> - Your latest actions</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { verticalTab: 'my-activity' },
    },
    // Step 6: Items in Review
    {
      target: '.items-in-review-panel',
      title: 'Items in Review',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Certificates that need your attention.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Each item shows risk level, compliance score, and exposure gap. Click to expand details or navigate to the certificate.
          </Typography>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 8,
      data: { verticalTab: 'my-activity', activityTab: 0 },
    },
    // Step 7: Flagged Items Tab
    {
      target: '.flagged-items-tab',
      title: 'Flagged Items',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            View items you've flagged for follow-up.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Flag important certificates or documents from anywhere in the app to track them here.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { verticalTab: 'my-activity', activityTab: 1 },
    },
    // Step 8: Recent Activity Tab
    {
      target: '.recent-activity-tab',
      title: 'Recent Activity',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Track your recent actions and mentions.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            See certificates you've worked on and comment mentions. Click any item to navigate directly.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { verticalTab: 'my-activity', activityTab: 2 },
    },
    // Step 9: Compliance Tab
    {
      target: '.dashboard-menu-compliance',
      title: 'Compliance View',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Switch to <strong>Compliance</strong> for detailed compliance analytics.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View vendor compliance status, certificate status distribution, and insurance type breakdown.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { verticalTab: 'my-activity' },
    },
    // Step 10: Compliance Charts
    {
      target: '.compliance-charts',
      title: 'Compliance Analytics',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Visual breakdown of compliance metrics:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Vendor Compliance</strong> - Status of all vendors</Typography></li>
            <li><Typography variant="body2"><strong>Certificate Compliance</strong> - Certificate status distribution</Typography></li>
            <li><Typography variant="body2"><strong>Insurance Types</strong> - Coverage type breakdown</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 8,
      data: { verticalTab: 'compliance' },
    },
    // Step 11: Expiring COIs Tab
    {
      target: '.dashboard-menu-quarterly',
      title: 'Expiring COIs',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Switch to <strong>Expiring COIs</strong> to manage upcoming expirations.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Filter by quarter and year, view expiring certificates, and take action before coverage lapses.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { verticalTab: 'compliance' },
    },
    // Step 12: Expiring COIs View
    {
      target: '.expiring-cois-container',
      title: 'Expiring Certificates',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Track and manage expiring certificates.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2">Filter by quarter and year</Typography></li>
            <li><Typography variant="body2">Filter by vendor, status, or policy type</Typography></li>
            <li><Typography variant="body2">Sort by expiration date to prioritize</Typography></li>
            <li><Typography variant="body2">Send renewal requests directly</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 8,
      data: { verticalTab: 'quarterly' },
    },
    // Step 13: Email Activity Tab
    {
      target: '.dashboard-menu-email-activity',
      title: 'Email Activity',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Switch to <strong>Email Activity</strong> to track email performance.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor email open rates, track vendor responsiveness, and view email history.
          </Typography>
        </TourContent>
      ),
      placement: 'right',
      spotlightPadding: 8,
      data: { verticalTab: 'quarterly' },
    },
    // Step 14: Email Activity Overview
    {
      target: '.email-activity-container',
      title: 'Email Analytics',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Email performance at a glance.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track sent emails, open rates, and engagement metrics. Filter by date range to analyze specific periods.
          </Typography>
        </TourContent>
      ),
      placement: 'top',
      spotlightPadding: 8,
      data: { verticalTab: 'email-activity', emailTab: 0 },
    },
    // Step 15: Email Activity Sub-tabs
    {
      target: '.email-activity-tabs',
      title: 'Email Views',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Different views of email activity:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2"><strong>Email Activity</strong> - Charts and summary metrics</Typography></li>
            <li><Typography variant="body2"><strong>Email Pulse</strong> - Real-time email timeline</Typography></li>
            <li><Typography variant="body2"><strong>Vendor Email Performance</strong> - Per-vendor stats</Typography></li>
          </Box>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { verticalTab: 'email-activity', emailTab: 0 },
    },
    // Step 16: Email Pulse
    {
      target: '.email-pulse-tab',
      title: 'Email Pulse',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Real-time email activity timeline.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            See when emails were sent, opened, and responded to. Track the status of pending requests.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { verticalTab: 'email-activity', emailTab: 1 },
    },
    // Step 17: Vendor Email Performance
    {
      target: '.vendor-email-performance-tab',
      title: 'Vendor Performance',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Email metrics by vendor.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compare response rates across vendors. Identify vendors who may need follow-up or alternative contact methods.
          </Typography>
        </TourContent>
      ),
      placement: 'bottom',
      spotlightPadding: 8,
      data: { verticalTab: 'email-activity', emailTab: 2 },
    },
    // Step 18: Tour Complete
    {
      target: '.dashboard-container',
      title: 'Tour Complete!',
      content: (
        <TourContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You now know how to use the <strong>Dashboard</strong>!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the sidebar to navigate between views. Check back regularly to stay on top of compliance and activity.
          </Typography>
        </TourContent>
      ),
      placement: 'center',
      spotlightPadding: 0,
      data: { verticalTab: 'my-activity' },
    },
  ],
};

/**
 * Get tour configuration by ID
 */
export const getTourById = (tourId) => {
  const tours = {
    [TOUR_IDS.ADD_CLIENT_MODAL]: ADD_CLIENT_MODAL_TOUR,
    [TOUR_IDS.CONFIGURE_CLIENT]: CONFIGURE_CLIENT_TOUR,
    [TOUR_IDS.COVERAGES]: COVERAGES_TOUR,
    [TOUR_IDS.CERTIFICATES]: CERTIFICATES_TOUR,
    [TOUR_IDS.ADD_SUBCONTRACTOR]: ADD_SUBCONTRACTOR_TOUR,
    [TOUR_IDS.SUBCONTRACTOR_DETAILS]: SUBCONTRACTOR_DETAILS_TOUR,
    [TOUR_IDS.REQUEST_DOCUMENTS]: REQUEST_DOCUMENTS_TOUR,
    [TOUR_IDS.DOCHUB]: DOCHUB_TOUR,
    [TOUR_IDS.DASHBOARD]: DASHBOARD_TOUR,
  };
  return tours[tourId];
};
